import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { cls } from "app/utils";
import { PackageCredit } from "app/utils/creditBundles";
import { setBookSession } from "app/actions/scheduling/bookSession";

import CoachCard from "./CoachCard";
import BookingTabs, { BookingTab } from "./BookingTabs";
import ByDateBody from "./ByDateBody";
import FacilityCalendarBody from "./FacilityCalendarBody";
import BookingFilters, {
  BookingFilterState,
  EMPTY_FILTERS,
} from "./BookingFilters";
import {
  matchesCoachSearch,
  orderCoachesForAthlete,
  topRateForAthlete,
} from "./coachMeta";
import useCoachNextSlots from "./useCoachNextSlots";
import styles from "./FacilityBooking.module.scss";

interface Props {
  coaches: CoachProfileModel[];
  timezone: string;
  packageCredits?: Map<number, PackageCredit>;
  /**
   * The in-app flow already has the app's Navbar for the back arrow and title,
   * so it suppresses this one rather than showing two.
   */
  showTitleRow?: boolean;
  /**
   * The in-app facility row. An athlete can belong to several facilities, which
   * is the chevron the frame draws beside the facility name. A public page is
   * always one facility and names it in the header strip above, so it passes
   * nothing and gets no row at all.
   */
  facilitySwitcher?: React.ReactNode;
  /**
   * Where choosing a coach, option or cell continues to. Defaults to the public
   * calendar; the in-app flow hands over to its own booking form.
   */
  bookingRoute?: (coach: CoachProfileModel) => string;
  /**
   * Where a chosen *slot* continues to — the coach's booking screen, which opens
   * on that slot with its confirm bar. Distinct from `bookingRoute`, which a
   * coach card uses to open the coach's page.
   */
  slotBookingRoute?: (coach: CoachProfileModel, rateId?: number) => string;
  /** False for the in-app flow: it queries a different scheduling surface. */
  isPublicPage?: boolean;
  /**
   * CFTB-3433: the rule the frame draws between the facility and the tabs. In-app
   * the facility switcher row carries its own rules; on a public page the facility
   * sits in the header strip above this component, so the rule belongs here.
   */
  showTopDivider?: boolean;
}

/**
 * The public facility booking page. Comment #31 fixes its default view as "By
 * Coach": pick a coach, then continue into their public calendar (frame
 * 526:28677). The Dates tab (frames 526:29419 / 526:29894) offers the same
 * facility by time instead, and Facility Calendar (526:29178) lays the whole
 * facility's day out as a grid.
 */
const publicCoachRoute = (coach: CoachProfileModel) =>
  `/public/availability?coach=${coach.calendarUuid}`;

/**
 * CFTB-3430: the bare coach link opens the coach's sessions, so a slot — which has
 * already settled which session it is — names it and lands on the calendar instead.
 */
const publicSlotRoute = (coach: CoachProfileModel, rateId?: number) =>
  rateId
    ? `/public/availability?coach=${coach.calendarUuid}&sessionId=${rateId}`
    : publicCoachRoute(coach);

const FacilityBooking: React.FC<Props> = ({
  coaches,
  timezone,
  packageCredits,
  showTitleRow = true,
  facilitySwitcher,
  bookingRoute = publicCoachRoute,
  slotBookingRoute = publicSlotRoute,
  isPublicPage = true,
  showTopDivider = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [query, setQuery] = useState("");

  // The tab lives in the URL so that coming back here — from a coach's page or
  // from checkout — returns to the tab the athlete was on rather than resetting
  // to Coaches.
  const tabInUrl = new URLSearchParams(location.search).get("tab");
  const [activeTab, setActiveTab] = useState<BookingTab>(
    tabInUrl === "dates" || tabInUrl === "facility-calendar"
      ? (tabInUrl as BookingTab)
      : "coaches",
  );

  const selectTab = (tab: BookingTab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(location.search);
    params.set("tab", tab);
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  /**
   * On a public page, only the coaches a visitor can actually open.
   *
   * The public coach endpoint is scoped `where(calendar_uuid:, public_calendar:
   * true)`, so a coach with their public calendar off 404s — and the page that
   * 404s used to send the visitor to "/", where pressing back returned to the
   * same dead link and bounced them again. The facility payload lists every
   * coach whatever their setting, so the list is what has to know.
   *
   * A coach with no upcoming availability is a different thing and stays: they
   * open, and their page says they have no slots, which is what a visitor
   * looking for that coach needs to be told.
   */
  const bookableCoaches = useMemo(
    () =>
      isPublicPage
        ? coaches.filter((coach) => coach.publicCalendar !== false)
        : coaches,
    [coaches, isPublicPage],
  );

  const [filters, setFilters] = useState<BookingFilterState>(EMPTY_FILTERS);

  const visibleCoaches = useMemo(
    () =>
      orderCoachesForAthlete(
        bookableCoaches.filter((coach) => matchesCoachSearch(coach, query)),
        packageCredits,
      ),
    [bookableCoaches, query, packageCredits],
  );

  // Asked for every coach at the facility rather than the searched subset, so typing
  // in the search box filters cards that already carry their time instead of
  // refetching availability on each keystroke.
  const { nextSlots, loading: nextSlotsLoading } = useCoachNextSlots(
    bookableCoaches,
    isPublicPage,
  );

  const openCoach = (coach: CoachProfileModel) => {
    // #31: the coach calendar opens with the top booking option pre-selected —
    // a package the athlete holds first, otherwise the coach's own order. Left
    // to itself the calendar would pre-select whichever option carries the
    // `defaultRate` flag, which ignores packages entirely.
    const topRate = topRateForAthlete(coach, packageCredits);
    if (topRate) {
      dispatch(
        setBookSession({
          coach,
          pricingValue: topRate,
          selectedSession: topRate,
          duration: topRate.durationInMinutes,
        }),
      );
    }
    history.push(bookingRoute(coach));
  };

  return (
    <div
      className={cls(styles.container, {
        [styles.container_public]: isPublicPage,
      })}
    >
      {showTitleRow && (
      <div
        className={cls(
          styles.title_row,
          isPublicPage && styles.title_row_public,
        )}
      >
        {/*
          The heading alone on a public page: 526:57411, 528:58537 and 528:61284
          all carry "Book a Session" over the tabs and nothing beside it — no back
          arrow (a public link is opened cold, with nothing behind it) and no
          timezone.
        */}
        {!isPublicPage && (
          <button
            type="button"
            className={styles.back_button}
            onClick={() => history.goBack()}
            aria-label={t("Back")}
            data-testid="book-by-coach-back"
          >
            <i className="ico ico-back" />
          </button>
        )}
        <h1 className={styles.title}>{t("Book a Session")}</h1>
        {!isPublicPage && timezone !== "" && (
          <div className={styles.timezone}>
            <i className="ico ico-earth" />
            <span>{timezone}</span>
          </div>
        )}
      </div>
      )}

      {/*
        Only the logged-in screen names the facility here. On a public page the
        facility already owns the header strip above — logo, name and, when it
        has a cover, the full-bleed banner — and the frame puts nothing between
        "Book a Session" and the tabs.
      */}
      {facilitySwitcher && (
        // The switcher draws the whole block the frame shows here — logo, name,
        // chevron and the counts under the name — so this row is only the rule
        // above and below it.
        <div className={styles.facility_row}>{facilitySwitcher}</div>
      )}

      {showTopDivider && <div className={styles.top_divider} />}

      <div className={styles.filter_row}>
        <BookingTabs
          tabs={[
            { id: "coaches", label: t("Coaches") },
            { id: "dates", label: t("Dates") },
            { id: "facility-calendar", label: t("Facility Calendar") },
          ]}
          active={activeTab}
          onSelect={selectTab}
        />

        {/* Present on all three tabs, as the public frames draw it. On Coaches it
            filters the grid; on Dates it filters which coaches' options appear
            under a slot. */}
        <div className={styles.search}>
          <i className={`ico ico-search ${styles.search_icon}`} />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("Search by name or title...")}
            aria-label={t("Search by name or title...")}
            data-testid="coach-search"
          />
        </div>
      </div>

      {/* The frame gives both time-based tabs this row. Coaches has the search
          instead — filtering people by name is what it needs. */}
      {activeTab !== "coaches" && (
        <BookingFilters
          coaches={coaches}
          filters={filters}
          onChange={setFilters}
          /**
           * Sessions or clinics is a question only the Facility Calendar asks — it
           * is the tab that draws both — and only in the app.
           *
           * CFTB-3451 took the pill off the public Facility Calendar; keying it to
           * the tab alone, as this did, put it back there. Both halves of that
           * ticket live in this one condition now.
           */
          showSessionType={activeTab === "facility-calendar" && !isPublicPage}
        />
      )}

      {activeTab === "dates" ? (
        <ByDateBody
          coaches={bookableCoaches}
          visibleCoachIds={visibleCoaches.map((coach) => coach.id)}
          packageCredits={packageCredits}
          /*
            CFTB-3448: the *slot* route, as the Facility Calendar below already gets.
            `ByDateBody` confirms with `bookingRoute(coach, rate.id)`, and
            `publicCoachRoute` takes only the coach — so the id was silently discarded
            and the calendar opened on no session at all. Its own default appended the
            id correctly; this override was the only thing dropping it.
          */
          bookingRoute={slotBookingRoute}
          isPublicPage={isPublicPage}
          filters={filters}
        />
      ) : activeTab === "facility-calendar" ? (
        <FacilityCalendarBody
          coaches={bookableCoaches}
          visibleCoachIds={visibleCoaches.map((coach) => coach.id)}
          packageCredits={packageCredits}
          bookingRoute={slotBookingRoute}
          isPublicPage={isPublicPage}
          filters={filters}
        />
      ) : visibleCoaches.length === 0 ? (
        <div className={styles.empty_state}>
          {/* A facility can have no bookable coaches at all, which is not the
              same as a search that matched none of them. */}
          {coaches.length === 0
            ? t("This facility has no coaches available for booking.")
            : t("No coaches match your search.")}
        </div>
      ) : (
        <div className={styles.coach_grid}>
          {visibleCoaches.map((coach) => (
            <CoachCard
              key={coach.id || coach.calendarUuid}
              coach={coach}
              packageCredits={packageCredits}
              nextSlot={nextSlots.get(coach.id) ?? null}
              nextSlotLoading={nextSlotsLoading}
              onSelect={openCoach}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FacilityBooking;
